import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Calendar,
  Users,
  Play,
  Star,
  TrendingUp,
  DollarSign,
  Clock,
  Video,
  FileText,
  Upload,
  ChevronRight,
  Loader2,
  BarChart3,
  Award,
  Eye,
  MessageSquare,
  ArrowUpRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SpeakerDashboard() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [webinars, setWebinars] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/api/admin/speaker-dashboard/dashboard`, { headers });
      setDashboardData(response.data);
      setWebinars([
        ...(response.data.upcoming_webinars || []),
        ...(response.data.recent_webinars || [])
      ]);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-700" />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-zinc-50 pt-24">
        <div className="container-custom py-12 text-center">
          <h1 className="font-heading text-2xl text-zinc-950 mb-4">Speaker Dashboard</h1>
          <p className="text-zinc-500 mb-6">You need speaker access to view this page.</p>
          <Link to="/join-speaker">
            <Button className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl">
              Apply to Become a Speaker
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const stats = [
    { 
      label: 'Total Webinars', 
      value: dashboardData.total_webinars || 0, 
      icon: Video,
      color: 'bg-blue-100 text-blue-600'
    },
    { 
      label: 'Upcoming Sessions', 
      value: dashboardData.upcoming_count || 0, 
      icon: Calendar,
      color: 'bg-green-100 text-green-600'
    },
    { 
      label: 'Total Attendees', 
      value: dashboardData.total_attendees || 0, 
      icon: Users,
      color: 'bg-purple-100 text-purple-600'
    },
    { 
      label: 'This Month', 
      value: dashboardData.month_attendees || 0, 
      icon: TrendingUp,
      color: 'bg-emerald-100 text-emerald-700'
    },
  ];

  return (
    <div className="min-h-screen bg-zinc-50 pt-20" data-testid="speaker-dashboard">
      {/* Header */}
      <section className="bg-zinc-950 py-12">
        <div className="container-custom">
          <div className="flex items-center justify-between">
            <div>
              <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 mb-4">
                <Award className="w-3 h-3 mr-1" />
                Speaker Portal
              </Badge>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
                Welcome back, {dashboardData.speaker?.first_name || user?.first_name}!
              </h1>
              <p className="text-zinc-400">
                Manage your webinars, track performance, and engage with your audience.
              </p>
            </div>
            <img
              src={dashboardData.speaker?.profile_image || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200'}
              alt="Speaker"
              className="w-24 h-24 rounded-2xl object-cover border-4 border-emerald-400 shadow-xl hidden md:block"
            />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 -mt-8">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, idx) => (
              <Card key={idx} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-zinc-500 text-sm mb-1">{stat.label}</p>
                      <p className="text-3xl font-bold text-zinc-950">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="w-6 h-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8">
        <div className="container-custom">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white shadow-sm border border-zinc-200 rounded-xl p-1">
              <TabsTrigger value="overview" className="data-[state=active]:bg-zinc-100 rounded-lg">
                <BarChart3 className="w-4 h-4 mr-2" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="webinars" className="data-[state=active]:bg-zinc-100 rounded-lg">
                <Video className="w-4 h-4 mr-2" />
                My Webinars
              </TabsTrigger>
              <TabsTrigger value="materials" className="data-[state=active]:bg-zinc-100 rounded-lg">
                <FileText className="w-4 h-4 mr-2" />
                Materials
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Upcoming Webinars */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-zinc-950">
                        <Calendar className="w-5 h-5 text-green-500" />
                        Upcoming Webinars
                      </span>
                      <Badge variant="secondary">{dashboardData.upcoming_webinars?.length || 0}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboardData.upcoming_webinars?.length > 0 ? (
                      <div className="space-y-4">
                        {dashboardData.upcoming_webinars.map((webinar) => (
                          <div key={webinar.id} className="flex items-center gap-4 p-4 bg-zinc-50 rounded-xl hover:bg-zinc-100 transition-colors">
                            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                              <Play className="w-6 h-6 text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-zinc-950 truncate">{webinar.title}</p>
                              <p className="text-sm text-zinc-500">
                                {webinar.date ? format(new Date(webinar.date), 'MMM d, yyyy h:mm a') : 'TBD'}
                              </p>
                            </div>
                            <div className="text-right">
                              <Badge className="bg-green-100 text-green-700">
                                <Users className="w-3 h-3 mr-1" />
                                {webinar.registration_count || 0}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-zinc-500">
                        <Calendar className="w-12 h-12 mx-auto mb-3 text-zinc-400" />
                        <p>No upcoming webinars scheduled</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Webinars */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-zinc-950">
                        <Clock className="w-5 h-5 text-blue-500" />
                        Recent Sessions
                      </span>
                      <Badge variant="secondary">{dashboardData.recent_webinars?.length || 0}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboardData.recent_webinars?.length > 0 ? (
                      <div className="space-y-4">
                        {dashboardData.recent_webinars.map((webinar) => (
                          <div key={webinar.id} className="flex items-center gap-4 p-4 bg-zinc-50 rounded-xl hover:bg-zinc-100 transition-colors">
                            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                              <Video className="w-6 h-6 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-zinc-950 truncate">{webinar.title}</p>
                              <p className="text-sm text-zinc-500">
                                {webinar.date ? format(new Date(webinar.date), 'MMM d, yyyy') : 'N/A'}
                              </p>
                            </div>
                            <div className="text-right">
                              <Badge variant="outline">
                                <Eye className="w-3 h-3 mr-1" />
                                {webinar.views || 0}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-zinc-500">
                        <Video className="w-12 h-12 mx-auto mb-3 text-zinc-400" />
                        <p>No completed webinars yet</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Performance Card */}
              <Card className="border-0 shadow-lg bg-zinc-900 text-white">
                <CardContent className="p-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-heading text-xl font-semibold mb-2">Your Performance</h3>
                      <p className="text-zinc-400 mb-4">
                        Track your growth and engagement metrics over time.
                      </p>
                      <div className="flex gap-6">
                        <div>
                          <p className="text-3xl font-bold text-emerald-400">{dashboardData.avg_rating?.toFixed(1) || '4.8'}</p>
                          <p className="text-zinc-400 text-sm flex items-center gap-1">
                            <Star className="w-3 h-3 fill-emerald-400 text-emerald-400" />
                            Avg. Rating
                          </p>
                        </div>
                        <div>
                          <p className="text-3xl font-bold text-emerald-400">{dashboardData.total_reviews || 0}</p>
                          <p className="text-zinc-400 text-sm flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            Reviews
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="hidden md:block">
                      <TrendingUp className="w-24 h-24 text-emerald-400/30" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="webinars" className="space-y-6">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-zinc-950">All My Webinars</CardTitle>
                </CardHeader>
                <CardContent>
                  {webinars.length > 0 ? (
                    <div className="space-y-4">
                      {webinars.map((webinar) => (
                        <div key={webinar.id} className="flex items-center gap-4 p-4 border border-zinc-200 rounded-xl hover:border-zinc-300 transition-colors">
                          <img 
                            src={webinar.thumbnail_url || 'https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=200'} 
                            alt={webinar.title}
                            className="w-20 h-14 rounded-lg object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-zinc-950 truncate">{webinar.title}</p>
                            <p className="text-sm text-zinc-500">
                              {webinar.date ? format(new Date(webinar.date), 'MMM d, yyyy h:mm a') : 'TBD'}
                            </p>
                          </div>
                          <Badge className={webinar.status === 'scheduled' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                            {webinar.status}
                          </Badge>
                          <div className="flex items-center gap-2 text-zinc-500">
                            <Users className="w-4 h-4" />
                            {webinar.registration_count || 0}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-zinc-500">
                      <Video className="w-16 h-16 mx-auto mb-4 text-zinc-400" />
                      <p className="mb-4">You haven't presented any webinars yet.</p>
                      <Link to="/contact">
                        <Button variant="outline" className="rounded-xl">
                          Contact Us to Schedule
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="materials" className="space-y-6">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-zinc-950">Presentation Materials</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12">
                    <Upload className="w-16 h-16 mx-auto mb-4 text-zinc-400" />
                    <h3 className="font-semibold text-zinc-950 mb-2">Upload Your Materials</h3>
                    <p className="text-zinc-500 mb-6 max-w-md mx-auto">
                      Share presentation slides, handouts, and supplementary materials with your attendees.
                    </p>
                    <Button className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Materials
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
